import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { AlertTriangle, Box, Camera, CheckCircle2, Download, FileText, FolderOpen, Receipt, ShieldCheck, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { UploadButtons } from './UploadButtons';
import { prepareFiles } from '../../lib/imageCompress';
import { fmtDate, fmtDateTime, fmtSize } from './orderConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUPS = [
  { key: 'scans', label: 'Empreintes & fichiers 3D', icon: Box, match: (f) => ['scan', 'archive'].includes(f.kind) },
  { key: 'photos', label: 'Photos & radios', icon: Camera, match: (f) => f.kind === 'photo' },
  { key: 'work_orders', label: 'Bons de travail', icon: FileText, match: (f) => f.kind === 'work_order' },
  { key: 'certificates', label: 'Certificats de conformité', icon: ShieldCheck, match: (f) => f.kind === 'certificate' },
  { key: 'lab_returns', label: 'Retours laboratoire', icon: Undo2, match: (f) => f.zone === 'out' },
  { key: 'others', label: 'Autres documents', icon: FolderOpen, match: () => true },
];

const groupFiles = (files) => {
  const used = new Set();
  return GROUPS.map((g) => {
    const items = files.filter((f) => !used.has(f.id) && g.match(f));
    items.forEach((f) => used.add(f.id));
    return { ...g, items };
  }).filter((g) => g.items.length > 0);
};

const FileRow = ({ f }) => (
  <li className="py-2 flex items-center justify-between gap-2" data-testid={`doc-row-${f.id}`}>
    <div className="min-w-0">
      <p className="text-sm font-medium truncate">{f.original_name}</p>
      <p className="text-xs text-muted-foreground">
        {f.order_number} · {fmtDateTime(f.uploaded_at)}{!f.purged && ` · ${fmtSize(f.size)}`}
      </p>
    </div>
    {f.purged ? (
      <Badge variant="outline" className="bg-gray-100 text-gray-500 border-gray-200 shrink-0" data-testid={`doc-expired-${f.id}`}>Expiré — supprimé</Badge>
    ) : (
      <a href={`${API}/orders/files/${f.id}/download`} target="_blank" rel="noreferrer" className="shrink-0">
        <Button size="icon" variant="ghost" data-testid={`doc-download-${f.id}`}><Download className="h-4 w-4" /></Button>
      </a>
    )}
  </li>
);

export const DocumentsCard = ({ orderId, refreshKey }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);

  const fetchDocs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}/documents`);
      setData(data);
    } catch (e) { /* dossier inaccessible : géré par le parent */ }
  }, [orderId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs, refreshKey]);

  const upload = async (fileList) => {
    const files = await prepareFiles(fileList);
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    setProgress(0);
    try {
      await axios.post(`${API}/orders/${orderId}/files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 50),
      });
      toast.success(`✓ ${files.length > 1 ? `${files.length} fichiers envoyés` : 'Fichier envoyé'}`, {
        icon: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
      });
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi — réessayez");
    } finally { setProgress(null); }
  };

  if (!data) return null;
  const groups = groupFiles(data.files || []);
  const nbDocs = (data.files || []).length + (data.client_invoices || []).length + (data.supplier_invoices || []).length;

  return (
    <Card className="border-border" data-testid="documents-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FolderOpen className="h-4 w-4" />Documents ({nbDocs})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {data.expiry && !data.expiry.purged && data.expiry.days_left <= 15 && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="expiry-warning">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Les fichiers de ce dossier seront supprimés dans <b>{data.expiry.days_left} jour(s)</b> ({fmtDate(data.expiry.expires_at)}). Téléchargez ce que vous souhaitez conserver.</span>
          </div>
        )}
        {data.expiry?.purged && (
          <div className="rounded-lg border border-border bg-secondary/50 p-3 text-sm text-muted-foreground" data-testid="expiry-purged-banner">
            Les fichiers de ce dossier ont été supprimés après 90 jours (politique de conservation). Les informations du dossier restent disponibles.
          </div>
        )}

        <div className="space-y-2">
          <UploadButtons onFiles={upload} disabled={progress !== null} />
          {progress !== null && (
            <div className="space-y-1" data-testid="upload-progress">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-muted-foreground">Envoi en cours... {progress}%</p>
            </div>
          )}
        </div>

        {groups.length === 0 && nbDocs === 0 && (
          <p className="text-sm text-muted-foreground text-center py-2">Aucun document pour le moment.</p>
        )}

        {groups.map((g) => (
          <div key={g.key} data-testid={`doc-group-${g.key}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-1">
              <g.icon className="h-3.5 w-3.5" />{g.label} ({g.items.length})
            </p>
            <ul className="divide-y divide-border">
              {g.items.map((f) => <FileRow key={f.id} f={f} />)}
            </ul>
          </div>
        ))}

        {(data.client_invoices || []).length > 0 && (
          <div data-testid="doc-group-invoices">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-1">
              <Receipt className="h-3.5 w-3.5" />Factures ({data.client_invoices.length})
            </p>
            <ul className="divide-y divide-border">
              {data.client_invoices.map((inv) => (
                <li key={inv.id} className="py-2 flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">{inv.invoice_number}</p>
                    <p className="text-xs text-muted-foreground">{fmtDate(inv.created_at)} · {inv.total?.toFixed(2)} €</p>
                  </div>
                  {inv.pdf_path && (
                    <a href={`${API}/client-invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer">
                      <Button size="icon" variant="ghost" data-testid={`doc-invoice-pdf-${inv.invoice_number}`}><Download className="h-4 w-4" /></Button>
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {user?.role === 'admin' && (data.supplier_invoices || []).length > 0 && (
          <div data-testid="doc-group-supplier-invoices">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-1">
              <Receipt className="h-3.5 w-3.5" />Factures fournisseur — admin ({data.supplier_invoices.length})
            </p>
            <ul className="divide-y divide-border">
              {data.supplier_invoices.map((inv) => (
                <li key={inv.id} className="py-2 text-sm">
                  {inv.invoice_number} <span className="text-muted-foreground text-xs">· {inv.fabricant_name} · {fmtDate(inv.created_at)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
